/**
 * Shared types for the active-organization feature. Plain types only (no
 * `"use server"` / `server-only`) so both server and client modules can import
 * them.
 */

/** A summary of one enterprise org the current user belongs to. */
export interface ActiveOrgSummary {
  /** `enterprise_organizations.id`. */
  id: number;
  name: string;
  logoUrl: string | null;
  /** True when the user is an admin of any group in this org. */
  isAdmin: boolean;
}

/**
 * Result of resolving the active org for the current request:
 *  - `none`       — user belongs to zero orgs (existing onboarding modal handles it).
 *  - `active`     — a single org is in effect (auto-selected when there's only one,
 *                   or matched from a valid cookie). `cookieSynced` is false when
 *                   the org was auto-selected but the `nw_active_org` cookie isn't
 *                   written yet (a Server Component render can't set it) — a client
 *                   effect persists it so the ZED backend stops falling back.
 *  - `needs-pick` — user belongs to 2+ orgs and has no valid active-org cookie;
 *                   the layout must render the picker gate instead of children.
 */
export type ResolveActiveOrgResult =
  | { status: "none" }
  | { status: "active"; org: ActiveOrgSummary; orgs: ActiveOrgSummary[]; cookieSynced: boolean }
  | { status: "needs-pick"; orgs: ActiveOrgSummary[] };

/**
 * Server-resolved active-org bootstrap handed to the enterprise shell /
 * `ActiveOrgProvider` as a single grouped prop.
 */
export interface ActiveOrgInit {
  /** All orgs the user belongs to (empty when active-org is off / 0 orgs). */
  orgs: ActiveOrgSummary[];
  /** The resolved active org id, or null (active-org off / 0-org). */
  activeOrgId: number | null;
}

/**
 * Whether an `orgId?` argument is a real active-org scope. `undefined`/`null`
 * (and non-positive values) mean "unscoped / legacy" — used by the org-scoped
 * data actions to choose the scoped vs. legacy query path.
 *
 * @param orgId - The optional org id threaded through scoped data actions.
 * @returns True when `orgId` is a positive integer scope.
 */
export function isScopedOrgId(orgId: number | null | undefined): orgId is number {
  return typeof orgId === "number" && orgId > 0;
}

/**
 * The org-selection rule shared by `resolveActiveOrg` (server-side, via `next/headers` cookies)
 * and its edge mirror in `resolve-landing-edge.ts` (which only has `NextRequest.cookies`) — kept
 * here, dependency-free, so both call sites share the one rule instead of re-deriving it.
 *
 * @param orgIds - Every org id the user belongs to.
 * @param preferredOrgId - The org id named by the caller's preferred signal (subdomain, explicit
 *   cookie, etc.), or null if absent/invalid.
 * @returns The active org id: the sole org for a 1-org user, the preferred org for a 2+ org user
 *   when it names one they belong to, or undefined (needs a pick) otherwise.
 */
export function pickActiveOrgId(orgIds: number[], preferredOrgId: number | null): number | undefined {
  return orgIds.length === 1 ? orgIds[0] : orgIds.find((id) => id === preferredOrgId);
}

/** Event-scoped routes whose `[event_id]` may belong to the org being left — shared by every
 *  org-switch entry point (`ActiveOrgContext.switchOrg`, `SidebarUserMenu`'s "Change organization",
 *  `SelectOrgGate`) so none of them can forget to navigate off an event route before refreshing. */
export const EVENT_ROUTE = /^\/dashboard\/(home|details|source|manage|notes)\/\d+/;

/**
 * Whether `pathname` is scoped to a specific event that may belong to the org being left. When
 * switching orgs from one of these routes, callers must navigate to `ORG_EVENTS_PATH` instead of
 * refreshing in place — otherwise the event stays on screen after its org is no longer active,
 * which `EventOrgSwitchGate` then "fixes" by silently switching the org back.
 *
 * @param pathname - The current pathname, or null/undefined.
 * @returns Whether the path names a specific event.
 */
export function isEventScopedRoute(pathname: string | null | undefined): boolean {
  return Boolean(pathname && EVENT_ROUTE.test(pathname));
}
